import React, { useState, useEffect, useRef, useCallback } from "react";
import { Check, Minus, ChevronDown } from "lucide-react";
import { suppliersApi } from "../api/suppliers";
import { useStudio } from "../store/useStudio";
import { colors, fonts } from "../theme";

// Maps an internal location id (e.g. facade_s, roof, balcony) to a surface tag for filtering.
function locTag(locId) {
  if (!locId) return "";
  if (locId.startsWith("facade")) return "facade";
  return locId;
}

// Minimal CSV parser: header row + comma-separated values, tolerant of quoted fields.
function parseCSV(text) {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length < 2) return [];
  const splitRow = (line) => {
    const out = []; let cur = ""; let q = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (c === '"') { if (q && line[i + 1] === '"') { cur += '"'; i++; } else q = !q; }
      else if (c === "," && !q) { out.push(cur); cur = ""; }
      else cur += c;
    }
    out.push(cur);
    return out.map((s) => s.trim());
  };
  const headers = splitRow(lines[0]).map((h) => h.toLowerCase());
  return lines.slice(1).map((line) => {
    const cells = splitRow(line);
    const row = {};
    headers.forEach((h, i) => { row[h] = cells[i] ?? ""; });
    const listify = (v) => (v ? v.split(/[;|]/).map((x) => x.trim()).filter(Boolean) : []);
    return {
      name: row.name || "",
      website: row.website || null,
      email: row.email || null,
      phone: row.phone || null,
      region: row.region || null,
      country: row.country || null,
      systems: listify(row.systems),
      locations: listify(row.locations),
      description: row.notes || row.description || null,
    };
  }).filter((r) => r.name);
}

export default function SupplierPanel({ systemLabel, systemGroup, locationId, region, systemId }) {
  const [suppliers, setSuppliers] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [minimized, setMinimized] = useState(true);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [searchNote, setSearchNote] = useState(null);
  const [webResults, setWebResults] = useState([]);
  const [uploadMsg, setUploadMsg] = useState(null);
  const fileRef = useRef(null);
  const pickKey = systemId || systemGroup || systemLabel || "system";
  const picked = useStudio((s) => s.pickedSuppliers[pickKey]);
  const setPickedSupplier = useStudio((s) => s.setPickedSupplier);
  const clearPickedSupplier = useStudio((s) => s.clearPickedSupplier);

  const query = systemLabel || systemGroup || "";
  const loc = locTag(locationId);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await suppliersApi.list(query, loc);
      setSuppliers(d.suppliers || []);
    } catch { setSuppliers([]); }
    finally { setLoading(false); }
  }, [query, loc]);

  useEffect(() => { load(); }, [load]);

  const runWebSearch = async () => {
    setSearching(true); setSearchNote(null); setWebResults([]);
    try {
      const d = await suppliersApi.search(query || "growing system", region || "");
      if (d.configured === false) setSearchNote(d.note);
      else if (d.error) setSearchNote(d.error);
      else if ((d.suppliers || []).length === 0) setSearchNote("No additional suppliers found for this region.");
      else setWebResults(d.suppliers);
    } catch { setSearchNote("Live search is temporarily unavailable."); }
    finally { setSearching(false); }
  };

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploadMsg(null);
    try {
      const rows = parseCSV(await f.text());
      if (!rows.length) { setUploadMsg("No valid rows found. Expected headers: name, website, email, phone, region, systems, notes."); return; }
      const d = await suppliersApi.add(rows);
      const skip = d.skipped ? ` (${d.skipped} row${d.skipped === 1 ? "" : "s"} skipped)` : "";
      setUploadMsg(`Added ${d.count} supplier${d.count === 1 ? "" : "s"}${skip}.`);
      await load();
    } catch { setUploadMsg("Could not read that file. Please upload a .csv."); }
    finally { if (fileRef.current) fileRef.current.value = ""; }
  };

  const pick = (s, fromWeb = false) => setPickedSupplier(pickKey, {
    id: s.id || s.name, name: s.name, website: s.website || null, email: s.email || null,
    phone: s.phone || null, region: s.region || s.country || null, fromWeb,
  });

  return (
    <div style={styles.wrap} data-testid="supplier-panel">
      <div style={styles.head}>
        <span style={styles.title}>Local suppliers</span>
        <span style={styles.count} data-testid="supplier-count">{suppliers.length}</span>
        <span style={styles.devBadge} data-testid="supplier-dev-badge">Under development</span>
        <button type="button" style={styles.minBtn} data-testid="supplier-minimize" title={minimized ? "Expand" : "Minimize"} onClick={() => setMinimized((v) => !v)}>
          {minimized ? <ChevronDown size={14} /> : <Minus size={14} />}
        </button>
      </div>

      {picked && (
        <div style={styles.pickedBar} data-testid="supplier-picked">
          <Check size={13} style={{ color: "#00B26A", flexShrink: 0 }} />
          <span style={styles.pickedName}>Selected: {picked.name}</span>
          <button type="button" style={styles.pickedChange} data-testid="supplier-clear-pick" onClick={() => clearPickedSupplier(pickKey)}>Change</button>
        </div>
      )}

      {minimized ? null : (
      <>
      <div style={styles.sub}>Matched to {query ? `“${query}”` : "your system"}{loc ? ` · ${loc}` : ""}. Select one to source from and move on.</div>

      {loading ? (
        <div style={styles.empty}>Loading suppliers…</div>
      ) : suppliers.length === 0 ? (
        <div style={styles.empty}>No matches in the directory yet. Upload your own or search the web below.</div>
      ) : (
        <div style={styles.list}>
          {suppliers.map((s) => (
            <div key={s.id} style={{ ...styles.card, ...(picked?.id === (s.id || s.name) ? styles.cardPicked : {}) }} data-testid={`supplier-${s.id}`}>
              <div style={styles.cardHead}>
                <button
                  type="button"
                  style={styles.cardHeadMain}
                  onClick={() => setOpenId(openId === s.id ? null : s.id)}
                  data-testid={`supplier-toggle-${s.id}`}
                >
                  <span style={styles.name}>
                    {s.name}
                    {s.user_added && <span style={styles.badgeUser}>Yours</span>}
                  </span>
                  <span style={styles.region}>{s.region || s.country || ""}</span>
                </button>
                <button
                  type="button"
                  style={picked?.id === (s.id || s.name) ? styles.selectedBtn : styles.selectBtn}
                  data-testid={`supplier-select-${s.id}`}
                  onClick={() => pick(s)}
                >
                  {picked?.id === (s.id || s.name) ? <><Check size={12} /> Selected</> : "Select"}
                </button>
              </div>
              {openId === s.id && (
                <div style={styles.detail} data-testid={`supplier-detail-${s.id}`}>
                  {s.description && <div style={styles.desc}>{s.description}</div>}
                  {s.systems?.length > 0 && (
                    <div style={styles.tags}>
                      {s.systems.slice(0, 6).map((t) => <span key={t} style={styles.tag}>{t}</span>)}
                    </div>
                  )}
                  <div style={styles.contactRows}>
                    {s.email && <Row label="Email"><a href={`mailto:${s.email}`} style={styles.link} data-testid={`supplier-email-${s.id}`}>{s.email}</a></Row>}
                    {s.phone && <Row label="Phone"><a href={`tel:${s.phone.replace(/[^+\d]/g, "")}`} style={styles.link}>{s.phone}</a></Row>}
                    {s.website && <Row label="Website"><a href={s.website} target="_blank" rel="noreferrer" style={styles.link} data-testid={`supplier-website-${s.id}`}>{s.website.replace(/^https?:\/\//, "").replace(/\/$/, "")}</a></Row>}
                    {!s.email && s.contact_url && <Row label="Contact"><a href={s.contact_url} target="_blank" rel="noreferrer" style={styles.link}>Contact page</a></Row>}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div style={styles.actions}>
        <button type="button" style={styles.actionBtn} onClick={runWebSearch} disabled={searching} data-testid="supplier-web-search">
          {searching ? "Searching the web…" : "Search the web for more"}
        </button>
        <button type="button" style={styles.actionBtn} onClick={() => fileRef.current?.click()} data-testid="supplier-upload-btn">
          Upload suppliers (.csv)
        </button>
        <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={onUpload} style={{ display: "none" }} data-testid="supplier-upload-input" />
      </div>
      {uploadMsg && <div style={styles.note} data-testid="supplier-upload-msg">{uploadMsg}</div>}
      {searchNote && <div style={styles.note} data-testid="supplier-search-note">{searchNote}</div>}
      {webResults.length > 0 && (
        <div style={styles.webWrap} data-testid="supplier-web-results">
          <div style={styles.webLabel}>FROM THE WEB</div>
          {webResults.map((r, i) => (
            <div key={i} style={styles.webCard}>
              <div style={styles.webCardTop}>
                <div style={styles.name}>{r.name}</div>
                <button type="button" style={picked?.id === r.name ? styles.selectedBtn : styles.selectBtn} data-testid={`supplier-select-web-${i}`} onClick={() => pick(r, true)}>
                  {picked?.id === r.name ? <><Check size={12} /> Selected</> : "Select"}
                </button>
              </div>
              {r.region && <div style={styles.region}>{r.region}</div>}
              <div style={styles.contactRows}>
                {r.email && <Row label="Email"><a href={`mailto:${r.email}`} style={styles.link}>{r.email}</a></Row>}
                {r.phone && <Row label="Phone"><span style={styles.link}>{r.phone}</span></Row>}
                {r.website && <Row label="Website"><a href={r.website} target="_blank" rel="noreferrer" style={styles.link}>{String(r.website).replace(/^https?:\/\//, "")}</a></Row>}
              </div>
            </div>
          ))}
        </div>
      )}
      </>
      )}
    </div>
  );
}

const Row = ({ label, children }) => (
  <div style={styles.row}><span style={styles.rowLabel}>{label}</span><span style={styles.rowVal}>{children}</span></div>
);

const styles = {
  wrap: { border: `1px solid ${colors.border}`, borderRadius: 12, padding: "12px 14px", background: "rgba(0,0,0,0.015)" },
  head: { display: "flex", alignItems: "center", gap: 8 },
  title: { fontFamily: fonts.sans, fontSize: 12.5, fontWeight: 700, color: colors.textPrimary },
  count: { fontFamily: fonts.mono, fontSize: 10.5, fontWeight: 700, color: "#fff", background: colors.pillar.systems, borderRadius: 999, padding: "1px 8px" },
  devBadge: { fontFamily: fonts.mono, fontSize: 8.5, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", color: "#9a6a00", background: "rgba(245,180,20,0.16)", border: "1px solid rgba(245,180,20,0.55)", borderRadius: 5, padding: "1px 6px", marginLeft: "auto" },
  sub: { fontFamily: fonts.sans, fontSize: 11, color: colors.textTertiary, marginTop: 3, lineHeight: 1.4 },
  empty: { fontFamily: fonts.sans, fontSize: 11.5, color: colors.textTertiary, padding: "10px 0" },
  list: { display: "flex", flexDirection: "column", gap: 6, marginTop: 9 },
  card: { border: `1px solid ${colors.border}`, borderRadius: 9, background: "#fff", overflow: "hidden" },
  cardHead: { display: "flex", alignItems: "center", gap: 6, padding: "5px 7px 5px 4px" },
  cardHeadMain: { flex: 1, minWidth: 0, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "3px 7px", border: "none", background: "transparent", cursor: "pointer", textAlign: "left" },
  cardPicked: { borderColor: "#00B26A", boxShadow: "0 0 0 1.5px rgba(0,178,106,0.25)" },
  minBtn: { width: 24, height: 24, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 7, border: "none", background: "rgba(0,0,0,0.05)", color: colors.textSecondary, cursor: "pointer" },
  selectBtn: { flexShrink: 0, display: "inline-flex", alignItems: "center", gap: 4, padding: "5px 12px", borderRadius: 999, border: `1px solid ${colors.pillar.systems}`, background: "#fff", fontFamily: fonts.sans, fontSize: 11, fontWeight: 700, color: colors.pillar.systems, cursor: "pointer" },
  selectedBtn: { flexShrink: 0, display: "inline-flex", alignItems: "center", gap: 4, padding: "5px 11px", borderRadius: 999, border: "1px solid #00B26A", background: "rgba(0,178,106,0.12)", fontFamily: fonts.sans, fontSize: 11, fontWeight: 700, color: "#00875A", cursor: "pointer" },
  pickedBar: { display: "flex", alignItems: "center", gap: 7, marginTop: 8, padding: "6px 9px", borderRadius: 8, background: "rgba(0,178,106,0.1)", border: "1px solid rgba(0,178,106,0.35)" },
  pickedName: { flex: 1, fontFamily: fonts.sans, fontSize: 11.5, fontWeight: 700, color: "#00875A", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  pickedChange: { border: "none", background: "transparent", color: colors.textSecondary, fontFamily: fonts.sans, fontSize: 10.5, fontWeight: 600, cursor: "pointer", textDecoration: "underline", flexShrink: 0 },
  webCardTop: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 },
  name: { fontFamily: fonts.sans, fontSize: 12.5, fontWeight: 600, color: colors.textPrimary, display: "inline-flex", alignItems: "center", gap: 6 },
  region: { fontFamily: fonts.sans, fontSize: 10.5, color: colors.textTertiary, flexShrink: 0 },
  badgeUser: { fontFamily: fonts.mono, fontSize: 8.5, fontWeight: 700, letterSpacing: 0.4, color: colors.pillar.resources, border: `1px solid ${colors.pillar.resources}`, borderRadius: 5, padding: "1px 5px" },
  detail: { padding: "0 11px 11px", borderTop: `1px solid ${colors.border}` },
  desc: { fontFamily: fonts.sans, fontSize: 11.5, color: colors.textSecondary, lineHeight: 1.5, margin: "9px 0" },
  tags: { display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 9 },
  tag: { fontFamily: fonts.mono, fontSize: 9, color: colors.textSecondary, background: colors.canvas, borderRadius: 5, padding: "2px 6px" },
  contactRows: { display: "flex", flexDirection: "column", gap: 5 },
  row: { display: "flex", gap: 8, alignItems: "baseline" },
  rowLabel: { fontFamily: fonts.mono, fontSize: 9.5, letterSpacing: 0.4, color: colors.textTertiary, width: 52, flexShrink: 0, textTransform: "uppercase" },
  rowVal: { fontFamily: fonts.sans, fontSize: 12, color: colors.textPrimary, wordBreak: "break-word" },
  link: { color: colors.brandDeep, textDecoration: "none", fontWeight: 600 },
  actions: { display: "flex", gap: 7, marginTop: 10, flexWrap: "wrap" },
  actionBtn: { flex: 1, minWidth: 130, padding: "7px 10px", borderRadius: 999, border: `1px solid ${colors.borderStrong}`, background: "#fff", fontFamily: fonts.sans, fontSize: 11.5, fontWeight: 600, color: colors.textSecondary, cursor: "pointer" },
  note: { fontFamily: fonts.sans, fontSize: 11, color: colors.textTertiary, marginTop: 8, lineHeight: 1.4 },
  webWrap: { marginTop: 10, borderTop: `1px dashed ${colors.border}`, paddingTop: 9, display: "flex", flexDirection: "column", gap: 6 },
  webLabel: { fontFamily: fonts.mono, fontSize: 9, letterSpacing: 1, color: colors.textTertiary, fontWeight: 700 },
  webCard: { border: `1px solid ${colors.border}`, borderRadius: 9, background: "#fff", padding: "8px 11px" },
};
